import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const FeaturesEditor = ({ content, onContentChange, onMultipleContentChanges, site, onCreateCustomTheme }) => {
  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...(content.features || [])];
    updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
    onContentChange('features', updatedFeatures);
  };

  const handleBenefitChange = (featureIndex, benefitIndex, value) => {
    const updatedFeatures = [...(content.features || [])];
    const updatedBenefits = [...(updatedFeatures[featureIndex].benefits || [])];
    updatedBenefits[benefitIndex] = value;
    updatedFeatures[featureIndex] = { ...updatedFeatures[featureIndex], benefits: updatedBenefits };
    onContentChange('features', updatedFeatures);
  };

  const addFeature = () => {
    const newFeature = {
      title: '',
      description: '',
      icon: 'star',
      benefits: ['']
    };
    onContentChange('features', [...(content.features || []), newFeature]);
  };

  const removeFeature = (index) => {
    const updatedFeatures = (content.features || []).filter((_, i) => i !== index);
    onContentChange('features', updatedFeatures);
  };

  const addBenefit = (featureIndex) => {
    const updatedFeatures = [...(content.features || [])];
    updatedFeatures[featureIndex].benefits = [...(updatedFeatures[featureIndex].benefits || []), ''];
    onContentChange('features', updatedFeatures);
  };

  const removeBenefit = (featureIndex, benefitIndex) => {
    const updatedFeatures = [...(content.features || [])];
    updatedFeatures[featureIndex].benefits = updatedFeatures[featureIndex].benefits.filter((_, i) => i !== benefitIndex);
    onContentChange('features', updatedFeatures);
  };

  const iconOptions = [
    { value: 'star', label: '⭐ Star' },
    { value: 'check', label: '✓ Check' },
    { value: 'heart', label: '♥ Heart' },
    { value: 'lightning', label: '⚡ Lightning' },
    { value: 'shield', label: '🛡️ Shield' },
    { value: 'target', label: '🎯 Target' },
    { value: 'rocket', label: '🚀 Rocket' },
    { value: 'diamond', label: '💎 Diamond' },
    { value: 'gear', label: '⚙️ Gear' },
    { value: 'trophy', label: '🏆 Trophy' }
  ];

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section title</label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          placeholder="Our Key Features"
        />
      </div>

      {/* Section Subtitle */}
      <div>
        <label className="block text-xs font-medium text-gray-900 mb-1">Section subtitle</label>
        <textarea
          rows={2}
          value={content.subtitle || ''}
          onChange={(e) => onContentChange('subtitle', e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="What sets us apart from the competition"
        />
      </div>

      {/* Features */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-900">Features</label>
          <button
            onClick={addFeature}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add feature
          </button>
        </div>

        <div className="space-y-3">
          {(content.features || []).map((feature, featureIndex) => (
            <div key={featureIndex} className="border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Feature {featureIndex + 1}</span>
                <button
                  onClick={() => removeFeature(featureIndex)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>

              {/* Feature Icon */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Icon</label>
                <select
                  value={feature.icon || 'star'}
                  onChange={(e) => handleFeatureChange(featureIndex, 'icon', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                >
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Feature Title */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Title</label>
                <input
                  type="text"
                  value={feature.title || ''}
                  onChange={(e) => handleFeatureChange(featureIndex, 'title', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Feature title"
                />
              </div>

              {/* Feature Description */}
              <div>
                <label className="block text-xs font-medium text-gray-900 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={feature.description || ''}
                  onChange={(e) => handleFeatureChange(featureIndex, 'description', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Describe the feature and its benefits"
                  maxLength={150}
                />
              </div>

              {/* Feature Benefits */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-900">Benefits</label>
                  <button
                    onClick={() => addBenefit(featureIndex)}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {(feature.benefits || []).map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={benefit}
                        onChange={(e) => handleBenefitChange(featureIndex, benefitIndex, e.target.value)}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Specific benefit"
                      />
                      <button
                        onClick={() => removeBenefit(featureIndex, benefitIndex)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {(content.features || []).length === 0 && (
          <div className="text-center py-4 border border-dashed border-gray-300 rounded">
            <p className="text-xs text-gray-500 mb-2">No features added yet</p>
            <button
              onClick={addFeature}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
            >
              <Plus className="w-3 h-3" />
              Add your first feature
            </button>
          </div>
        )}
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default FeaturesEditor;